import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, User, Heart, CalendarCheck, Star, Loader2, RefreshCw, ArrowRight } from "lucide-react";
import CustomerLayout from "@/components/customer/CustomerLayout";
import { MetricCard } from "@/components/dashboard/DashboardShell";
import { useAuth } from "@/context/AuthContext";
import apiClient from "@/lib/api";

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = () => {
    setLoading(true);
    setError(false);
    apiClient
      .get("/customer/dashboard/stats")
      .then((r) => setStats(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const completion = stats?.profile?.completion ?? 0;
  const activity = stats?.activity || { favorites: 0, bookings: 0, reviews: 0 };

  return (
    <CustomerLayout testid="customer-dashboard">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="customer-greeting">
            Halo, {user?.name || "Pencari Hunian"} 👋
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Selamat datang kembali. Kelola profil dan lanjutkan pencarian hunianmu.
          </p>
        </div>
        <button
          onClick={() => navigate("/search")}
          data-testid="customer-explore-cta"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-accent text-accent-foreground hover:scale-105 active:scale-95 transition-transform"
        >
          <Search size={16} /> Jelajahi Hunian
        </button>
      </div>

      {loading ? (
        <div className="mt-10 grid place-items-center py-16" data-testid="customer-dashboard-loading">
          <Loader2 className="animate-spin text-primary" size={28} />
        </div>
      ) : error ? (
        <div
          className="mt-8 rounded-2xl border border-destructive/30 bg-destructive/10 p-8 text-center"
          data-testid="customer-dashboard-error"
        >
          <p className="text-sm text-destructive">Gagal memuat ringkasan aktivitas.</p>
          <button
            onClick={load}
            data-testid="customer-dashboard-retry"
            className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            <RefreshCw size={14} /> Coba lagi
          </button>
        </div>
      ) : (
        <>
          {/* Profile completion */}
          <div className="mt-8 bg-card border border-border rounded-2xl p-6" data-testid="customer-profile-completion">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Kelengkapan Profil</p>
                <p className="mt-1 font-heading text-2xl font-bold" data-testid="customer-completion-value">{completion}%</p>
              </div>
              <button
                onClick={() => navigate("/customer/profile")}
                data-testid="customer-complete-profile-button"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-primary border border-border hover:bg-secondary/60 transition-colors"
              >
                <User size={15} /> Lengkapi Profil
              </button>
            </div>
            <div className="mt-4 h-2.5 w-full rounded-full bg-secondary overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${completion}%` }}
                data-testid="customer-completion-bar"
              />
            </div>
          </div>

          {/* Activity summary */}
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            <MetricCard testid="customer-metric-favorites" label="Favorit" value={String(activity.favorites)} hint="Hunian tersimpan" />
            <MetricCard testid="customer-metric-bookings" label="Booking Aktif" value={String(activity.bookings)} hint="Pemesanan berjalan" />
            <MetricCard testid="customer-metric-reviews" label="Ulasan" value={String(activity.reviews)} hint="Ulasan diberikan" />
          </div>

          {/* Shortcuts */}
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <button
              onClick={() => navigate("/customer/bookings")}
              data-testid="customer-shortcut-bookings"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 text-left hover:-translate-y-0.5 transition-transform"
            >
              <span className="flex items-center gap-3">
                <span className="grid place-items-center w-10 h-10 rounded-xl bg-secondary text-primary"><CalendarCheck size={18} /></span>
                <span>
                  <span className="block text-sm font-semibold">Booking Saya</span>
                  <span className="block text-xs text-muted-foreground">
                    {(activity.total_bookings ?? 0) > 0 ? `${activity.total_bookings} booking · ${activity.bookings} aktif` : "Belum ada booking."}
                  </span>
                </span>
              </span>
              <ArrowRight size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
            </button>
            <button
              onClick={() => navigate("/customer/favorites")}
              data-testid="customer-shortcut-favorites"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 text-left hover:-translate-y-0.5 transition-transform"
            >
              <span className="flex items-center gap-3">
                <span className="grid place-items-center w-10 h-10 rounded-xl bg-secondary text-primary"><Heart size={18} /></span>
                <span>
                  <span className="block text-sm font-semibold">Favorit</span>
                  <span className="block text-xs text-muted-foreground">Belum ada hunian favorit.</span>
                </span>
              </span>
              <ArrowRight size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
            </button>
          </div>
        </>
      )}
    </CustomerLayout>
  );
}
